import { Space } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { usePopupPortal } from "../../../../contexts/PopupContext";
import { getTrackingOp, setTrackingOp, state, useTrackingOp } from "../../../../contexts/TrackingOpContext";
import { EXECUTION_TYPE, OP_RUNNING_MODE } from "../../../../util/varibles/constants";
import { IActivityLog, IHarvest2 } from "../../../../util/varibles/interface";
import Icon, { ICON_NAME } from "../../../Icon";
import { PlanInfo } from "../../../Popup/PopupOpInfo/Info/Harvest/HarvestInfo";
import FinishStepButton from "../FinishStepButton";
import LiveWeather from "../LiveWeather";
import NextTabButton from "../NextTabButton";
import styles from "../style.module.scss";
import { finishHarvesting, startHarvesting } from "./HarvestingPopup";
import Tanks from "./Tanks";

const NextTab = ({ isDone }: { isDone: boolean }) => {
    const set = setTrackingOp();

    return <>
        {isDone && <NextTabButton
            name="Finish harvesting"
            onClick={() => {
                set(({ step }) => ({ step: step + 1 }));
            }}
        />}
    </>
}

export default function Harvesting({ log, stepIndex }: { log: IActivityLog, stepIndex: number }) {
    const { real_sub_steps = [], real_start_time, real_finish_time } = log;
    const [op] = useTrackingOp(state => state.op?.operation as IHarvest2);
    const { sub_operations = [] } = op || {};
    const { openPopup, popupHolder } = usePopupPortal();

    const get = getTrackingOp();
    const tanks = useRef([...(op?.tanks || [])]
        .sort((a, b) => a.name.localeCompare(b.name)));
    const isShowSiteName = useRef(
        new Set(sub_operations.map(item => item.site_id)).size > 1
    );
    const started = useMemo(() => real_sub_steps
        .filter(item => !!item.real_start_time && !item.real_finish_time), [real_sub_steps]);
    const runningUnitId = started[0]?.unit_id;
    const [selectedUnitId, setSelectedUnitId] = useState<string>();
    const unit = sub_operations.find(item => item.unit_id === selectedUnitId);
    const actionUnit = runningUnitId
        ? sub_operations.find(item => item.unit_id === runningUnitId)
        : unit;

    useEffect(() => {
        if (!sub_operations.some(item => item.unit_id === selectedUnitId))
            setSelectedUnitId(runningUnitId || sub_operations[0]?.unit_id);
    }, [runningUnitId, selectedUnitId, sub_operations]);

    const { op_running_mode = OP_RUNNING_MODE.NORMAL, execution_type } = op || {};
    const allUnitsLoaded = useMemo(() => sub_operations.length > 0 && sub_operations.every(item =>
        real_sub_steps.some(subStep =>
            subStep.unit_id === item.unit_id && !!subStep.real_finish_time
        )
    ), [real_sub_steps, sub_operations]);
    const isDone = useMemo(() => {
        if (execution_type === EXECUTION_TYPE.AUTO)
            return true;

        return op_running_mode === OP_RUNNING_MODE.DETAIL
            ? allUnitsLoaded
            : !!real_finish_time;
    }, [allUnitsLoaded, execution_type, op_running_mode, real_finish_time]);

    const nextStep = useMemo(() => {
        if (execution_type === EXECUTION_TYPE.AUTO)
            return null;

        if (op_running_mode !== OP_RUNNING_MODE.DETAIL) {
            if (real_finish_time)
                return null;

            let name = "Harvesting";
            const payload: {
                group_ids: string[],
                step_state: string,
                current_process: number,
                step_new?: number
            } = {
                group_ids: [],
                step_state: state[0],
                current_process: stepIndex
            };

            if (real_start_time) {
                name = "Finish Harvesting";
                payload.step_state = state[1];
                payload.step_new = -1;
            }

            return <FinishStepButton name={name} justify="start" payload={payload} />;
        }

        if (!actionUnit)
            return null;

        if (allUnitsLoaded)
            return null;

        const hasStartedLoading = real_sub_steps.some(subStep =>
            subStep.unit_id === actionUnit.unit_id && !!subStep.real_start_time
        );
        if (started.length === 0 && hasStartedLoading)
            return null;

        const willCompleteHarvesting = sub_operations.every(item =>
            item.unit_id === actionUnit.unit_id || real_sub_steps.some(subStep =>
                subStep.unit_id === item.unit_id && !!subStep.real_finish_time
            )
        );
        const onClick = started.length > 0
            ? () => finishHarvesting({
                status: 1,
                started,
                tanks: tanks.current,
                unit: actionUnit,
                log,
                current_process: stepIndex,
                step_new: willCompleteHarvesting ? -1 : undefined
            }, openPopup)
            : () => startHarvesting({
                status: 0,
                tanks: tanks.current,
                unit: actionUnit,
                current_process: stepIndex
            }, openPopup);

        return <FinishStepButton
            name={started.length > 0 ? "Finish Loading" : "Loading"}
            justify="start"
            onClick={onClick}
        />;
    }, [actionUnit, allUnitsLoaded, execution_type, log, op_running_mode, openPopup, real_finish_time, real_start_time, real_sub_steps, started, stepIndex, sub_operations]);
    console.log(op_running_mode)
    return <>
        <div className="w-full">
            <div className="mb-1">
                <LiveWeather vesselId={get().op?.vessel.id || ""} />
            </div>
            <div className={styles.units}>
                <div className="text-[14px] font-w4 pt-[2px] pr-2">
                    Harvest at:
                </div>
                {sub_operations.map(item => {
                    const { id, site_name, unit_id } = item;
                    const isDone = real_sub_steps.some(subStep =>
                        subStep.unit_id === unit_id && !!subStep.real_finish_time
                    );
                    return <div
                        key={id}
                        className={styles.unit}
                        data-active={unit?.id === id}
                        onClick={() => {
                            setSelectedUnitId(unit_id);
                        }}
                    >
                        {isShowSiteName.current && <div className="font-w4">{site_name}</div>}
                        <div className="font-w4">{unit_id}</div>
                        <div>{isDone && <Icon icon={ICON_NAME.CHECKED} bg="colorActive" />}</div>
                    </div>
                })}
            </div>
            <Space className="w-full pt-4" orientation="vertical" size={1} >
                <div className="text-[15px] font-w4" >Planned harvest info.</div>
                {unit && <PlanInfo data={unit} />}
                <div className="mt-[25px]">
                    <Tanks showLoading unitId={unit?.unit_id} />
                </div>
            </Space>
        </div>
        {nextStep || <NextTab isDone={isDone} />}
        {popupHolder}
    </>
}
